import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map, fromJS } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import _get from 'lodash/get'
import { useQuery } from '@apollo/client'
import {
  updateUpstreamContext,
  updateUpstreamContextFromObject,
  CONTEXT_NAME_ADMIN_TITLE,
} from '../../services/upstream-context/index'
import {
  TYPE_CONTENT_PERSON,
} from '../../services/content-type/index'
import { GET_CONTENT } from '../../services/content/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import {
  CONTENT_IMAGE_LIST,
  getFormattedContentData,
  getOrderedContent,
  mergeTabDataForCW3,
} from '../../services/content/index'
import Row, { STYLES } from '../Row.v2/index'
import RowV3 from '../Row.v3/index'
import TileV2Graphql from '../Tile.v2/Tile.v2Graphql/index'
import { EN } from '../../services/languages/constants'
import {
  TILE_V2_DISPLAY_ROUND,
  TILE_V2_DISPLAY_DEFAULT,
} from '../Tile.v2/constants'
import { TYPE_CONTINUE_WATCHING_V3 } from '../../services/pm-section/index'
import { get as getConfig } from '../../config/index'

const config = getConfig()
const rowV3Active = !!_get(config, ['features', 'rowV3'])

// Delay before showing placeholders - if data arrives within this time, skip placeholders
const PLACEHOLDER_DELAY_MS = 200

function TileV2GraphqlManager (props) {
  const {
    adminTitle,
    onRenderContent,
    onVisibilityChange,
    sectionIndex,
    titleGroupIds,
    upstreamContext,
    disableHover,
    uiLocation,
    language,
    fetchPolicy,
    authJwt,
    display,
    staticRow,
    touchable,
    asGrid,
    showNumbers,
    showStack,
    sectionType,
    section,
    displayMode,
    rowPlacement,
  } = props
  // Continue watching v3
  const isContinueWatchingV3 = sectionType === TYPE_CONTINUE_WATCHING_V3
  const [removedItems, setRemovedItems] = useState([])
  const [animatingIndex, setAnimatingIndex] = useState(null)
  const [showPlaceholder, setShowPlaceholder] = useState(false)
  const placeholderTimeoutRef = useRef(null)

  // query GraphQL for the data
  const { data, loading } = useQuery(GET_CONTENT, {
    variables: {
      language: [language, '_primary'],
      primaryLanguage: [language],
      titleGroupIds,
      imageType: CONTENT_IMAGE_LIST,
      limitedData: true,
      includeSeriesImage: isContinueWatchingV3,
      includeCourseProgress: true,
      learnerLevelLanguage: language,
    },
    fetchPolicy,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: authJwt,
    },
  })

  const getRowStyle = () => {
    const { reducedGrid, rowStyle } = props
    const hasProduct = titleGroupIds.some((id) => id.includes('product'))

    if (hasProduct) {
      return STYLES.TILES_PRODUCT
    }

    if (rowStyle) {
      return rowStyle
    }

    if (reducedGrid) {
      return STYLES.S12334
    }

    return STYLES.S12345
  }

  const contentList = _get(data, ['contentList', 'content'], []) || []
  // Safely access tabList - section.data is the app object,
  // section.data.data is the union type data
  // For ContentList type, tabs are at section.data.data.tabs
  // (see getPmSectionTabs in services/pm-section)
  // mergeTabDataForCW3 expects an Immutable List, so keep it as List
  let tabList = List()
  if (section && Map.isMap(section)) {
    try {
      const tabsData = section.getIn(['data', 'data', 'tabs', 0, 'tabContent'])
      if (tabsData) {
        if (List.isList(tabsData)) {
          tabList = tabsData
        } else if (Array.isArray(tabsData)) {
          tabList = List(tabsData)
        } else {
          tabList = List()
        }
      }
    } catch (e) {
      // If path doesn't exist or error accessing, tabList remains empty List
      tabList = List()
    }
  }

  // Ensure section is always a Map when passed to child components
  const safeSection = (section && Map.isMap(section)) ? section : Map()
  const orderedList = getOrderedContent(titleGroupIds, contentList, 'titleGroupId')
  const finalList = isContinueWatchingV3
    ? mergeTabDataForCW3(orderedList, tabList)
    : orderedList

  // create Immutable data for the underlying components
  // Filter out any null/undefined items and ensure all items are converted to Maps
  const immutableContentList = (fromJS(finalList) || List())
    .filter((item) => item != null && Map.isMap(item))
  const placeholderContentList = fromJS(titleGroupIds).map((i) => {
    return Map({ titleGroupId: i })
  })

  // Delay showing placeholders - if data arrives within 200ms, skip placeholders
  useEffect(() => {
    if (loading) {
      // Clear any existing timeout
      if (placeholderTimeoutRef.current) {
        clearTimeout(placeholderTimeoutRef.current)
      }
      // Set timeout to show placeholder after delay
      placeholderTimeoutRef.current = setTimeout(() => {
        setShowPlaceholder(true)
        placeholderTimeoutRef.current = null
      }, PLACEHOLDER_DELAY_MS)
    } else {
      // Data arrived - clear timeout and hide placeholder
      if (placeholderTimeoutRef.current) {
        clearTimeout(placeholderTimeoutRef.current)
        placeholderTimeoutRef.current = null
      }
      setShowPlaceholder(false)
    }

    // Cleanup timeout on unmount
    return () => {
      if (placeholderTimeoutRef.current) {
        clearTimeout(placeholderTimeoutRef.current)
        placeholderTimeoutRef.current = null
      }
    }
  }, [loading])

  // Function to remove a slide with animation
  const handleRemoveSlide = (index) => {
    // set animation state
    setAnimatingIndex(index)

    setTimeout(() => {
      setAnimatingIndex(null)
      setRemovedItems([...removedItems, index])
    }, 300)
  }

  const renderItem = (item, params) => {
    // Ensure item is valid before processing
    if (!item) {
      return null
    }

    const tileData = getFormattedContentData(item)

    // Ensure tileData is valid before accessing properties
    if (!tileData || !Map.isMap(tileData)) {
      return null
    }

    // Continue watching v3 remove functionality
    const itemIndex = isContinueWatchingV3 && Map.isMap(item) ? item.get('index') : params.index
    const isAnimating = isContinueWatchingV3 ? animatingIndex === itemIndex : false

    return (
      <TileV2Graphql
        key={params.index}
        data={tileData}
        upstreamContext={updateUpstreamContextFromObject(
          upstreamContext,
          {
            contentType: tileData.get('contentType'),
            itemIndex,
            contentId: tileData.get('contentId'),
          },
        )}
        sectionIndex={sectionIndex}
        rowPlacement={rowPlacement}
        adminTitle={adminTitle}
        index={itemIndex}
        uiLocation={uiLocation}
        display={tileData.get('contentType') === TYPE_CONTENT_PERSON ? TILE_V2_DISPLAY_ROUND : display}
        showNumbers={showNumbers}
        showStack={showStack}
        displayMode={displayMode}
        isContinueWatchingV3={isContinueWatchingV3}
        onRemove={isContinueWatchingV3 ? () => handleRemoveSlide(itemIndex) : () => {}}
        isAnimating={isAnimating}
        section={safeSection}
      />
    )
  }

  // Only show placeholders if loading AND the delay has elapsed
  const shouldShowPlaceholder = loading && showPlaceholder

  if (rowV3Active) {
    const items = shouldShowPlaceholder
      ? placeholderContentList
      : immutableContentList
        .filter((_item, i) => !removedItems.includes(i))
    return (
      <RowV3
        style={getRowStyle()}
        onRenderContent={onRenderContent}
        onVisibilityChange={(isVisible) => {
          onVisibilityChange(isVisible, items)
        }}
        isContinueWatchingV3={isContinueWatchingV3}
      >
        {items.map((item, index) => renderItem(item, { index }))}
      </RowV3>
    )
  }

  return (
    <Row
      className={touchable ? 'static-metadata' : null}
      items={shouldShowPlaceholder ? placeholderContentList : immutableContentList}
      upstreamContext={updateUpstreamContext(
        upstreamContext,
        CONTEXT_NAME_ADMIN_TITLE,
        adminTitle,
      )}
      createAccessor={() => null}
      style={getRowStyle()}
      onRenderContent={onRenderContent}
      onVisibilityChange={onVisibilityChange}
      rowPlacement={rowPlacement}
      disableScale={true}
      disableHover={disableHover}
      static={staticRow}
      asGrid={asGrid}
    >
      {(item, tileData) => renderItem(item, tileData)}
    </Row>
  )
}

TileV2GraphqlManager.propTypes = {
  titleGroupIds: PropTypes.array.isRequired,
  fetchPolicy: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  upstreamContext: ImmutablePropTypes.map.isRequired,
  adminTitle: PropTypes.string.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  rowPlacement: PropTypes.number.isRequired,
  disableHover: PropTypes.bool,
  reducedGrid: PropTypes.bool,
  onVisibilityChange: PropTypes.func,
  onRenderContent: PropTypes.func,
  rowStyle: PropTypes.string,
  uiLocation: PropTypes.string,
  display: PropTypes.string,
  staticRow: PropTypes.bool,
  touchable: PropTypes.bool,
  asGrid: PropTypes.bool,
  showNumbers: PropTypes.bool,
  showStack: PropTypes.bool,
}

TileV2GraphqlManager.defaultProps = {
  titleGroupIds: [],
  fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  upstreamContext: Map(),
  sectionIndex: 1,
  rowPlacement: 1,
  disableHover: false,
  reducedGrid: false,
  adminTitle: 'Unknown',
  rowStyle: STYLES.S12345,
  language: EN,
  display: TILE_V2_DISPLAY_DEFAULT,
  staticRow: false,
  asGrid: false,
  showNumbers: false,
  showStack: false,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        authJwt: state.auth.get('jwt'),
        touchable: state.app.getIn(['viewport', 'touchable'], false),
        auth: state.auth,
      }
    },
  ),
)(TileV2GraphqlManager)
